/**
 * Starts an exam-wrapper set from a deep link (`/practice?set=…`, pedagogy spec §4.3.9): fetches the
 * problems and builds what the runner needs, without touching storage or the router (the starter
 * component does that), so every rule is testable with a stubbed `fetch`.
 *
 * - `which-principle`: `/questions?mode=mixed&planOnly=true`, plan steps only. The state is its own
 *   (`planSet.ts`); the run is `WhichPrinciplePage` at `/practice/principles`.
 * - `timed-pair`: ten open problems in mixed mode, the two worth most points kept. Their plan offers
 *   are dropped and the session is created without `plans`: the clock is running and the exam has
 *   no plan step (§12 D2). The run is the ordinary practice session, with a `timed` clock and an
 *   `exam-wrapper` origin on each first try. No elapsed time is stored (§13 Q28).
 */
import { WHICH_PRINCIPLE_COUNT } from "@shared/examWrapper";
import type { PracticePreset, PracticeSet } from "../deepLink";
import { clampCount } from "../deepLink";
import { createSession, type PracticeSessionData } from "../session";
import type { AnswerOrigin } from "../types";
import { fetchExamPace, fetchQuestions, planOnlyQuery, timedPairQuery } from "./api";
import { createPlanSet, type PlanSetData } from "./planSet";
import { DEFAULT_PER_POINT, limitSecOf, perPointFromHistory, pickPair } from "./timedPair";

/** What a link asks a set for; the part of `PracticePreset` a set reads. */
export type SetPreset = Pick<PracticePreset, "chapters" | "count" | "perPoint" | "from"> & {
  set: PracticeSet;
};

export type StartedSet =
  | { kind: "which-principle"; data: PlanSetData }
  | { kind: "timed-pair"; session: PracticeSessionData }
  /** Nothing in the chosen moments qualifies yet. */
  | { kind: "empty"; set: PracticeSet };

/** The pace of a timed pair: the link's, else 0.8 × the last submitted exam's, else the default. */
async function paceOf(preset: SetPreset): Promise<number> {
  if (preset.perPoint !== undefined) return preset.perPoint;
  try {
    return perPointFromHistory(await fetchExamPace()) ?? DEFAULT_PER_POINT;
  } catch {
    return DEFAULT_PER_POINT;
  }
}

export async function startSet(preset: SetPreset, now: number = Date.now()): Promise<StartedSet> {
  if (preset.set === "which-principle") {
    const requested = preset.count === null ? WHICH_PRINCIPLE_COUNT : clampCount(preset.count);
    const res = await fetchQuestions(planOnlyQuery(preset.chapters, requested));
    const data = createPlanSet(res.questions, res.plans ?? {}, requested, preset.from);
    return data.questions.length === 0
      ? { kind: "empty", set: preset.set }
      : { kind: "which-principle", data };
  }

  const [res, perPoint] = await Promise.all([
    fetchQuestions(timedPairQuery(preset.chapters)),
    paceOf(preset),
  ]);
  const pair = pickPair(res.questions);
  if (pair.length < 2) return { kind: "empty", set: preset.set };
  const origin: AnswerOrigin = {
    kind: "exam-wrapper",
    set: "timed-pair",
    ...(preset.from ? { examSessionId: preset.from } : {}),
  };
  // No `plans`: a timed pair has no plan step, whatever mixed mode offered.
  const session = createSession("mixed", pair, undefined, {
    origins: Object.fromEntries(pair.map((question) => [question.id, origin])),
    set: { kind: "timed-pair", ...(preset.from ? { from: preset.from } : {}) },
    timed: { limitSec: limitSecOf(pair, perPoint), startedAt: now },
  });
  return { kind: "timed-pair", session };
}
